import { FormEvent, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { resetPassword } from '../api/auth'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Set a new password</h1>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-xl">
          {!token ? (
            <div className="space-y-5">
              <div className="rounded-lg bg-red-50 border border-red-300 text-red-700 px-4 py-3 text-sm">
                This link is missing its reset token. Request a new one below.
              </div>
              <p className="text-center text-sm text-gray-500">
                <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 font-medium">
                  Request a new reset link
                </Link>
              </p>
            </div>
          ) : done ? (
            <div className="space-y-5">
              <div className="rounded-lg bg-green-50 border border-green-300 text-green-800 px-4 py-3 text-sm">
                Password updated — you can now sign in.
              </div>
              <p className="text-center text-sm text-gray-500">
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in</Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-300 text-red-700 px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">New password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition px-3 py-2"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm new password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition px-3 py-2"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-blue-200 disabled:text-blue-500 text-white text-sm font-semibold py-2.5 transition"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
